'use client';

import { use } from 'react';
import {
  Divider,
  Hide,
  Show,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';

import {
  Button,
  SplashScreen,
  GiftcardActionButtons,
  MultiExpirationModal,
} from 'components';
import { useGiftcard } from 'context/giftcard';
import { useGiftcardsPagesLayoutContent } from 'context/giftcardsPagesLayoutContent';
import * as AppLayout from 'components/AppLayout';

const openSplashScreenGiftcardTypes: GiftcardType[] = [1000, 11000];

interface Props {
  searchParams: Promise<{ show_expires_info?: string; t: string }>;
}

export type SecretGiftcardPageProps = Props;

export default function SecretGiftcardPage(props: Props) {
  const { searchParams } = props;

  const { show_expires_info: showExpiresInfo = '' } = use(searchParams);

  const { giftcard } = useGiftcard();

  const openSplashScreen = openSplashScreenGiftcardTypes.includes(
    giftcard.type,
  );

  const isDepotCard = Boolean(giftcard.giftcardDepotId);

  const { isOpen, onClose } = useDisclosure({
    defaultIsOpen: openSplashScreen,
  });

  const openExpiresInfo = ['true', '1'].includes(showExpiresInfo);

  const {
    isOpen: isMultiExpirationsModalOpen,
    onClose: onCloseMultiExpirationsModal,
    onOpen: onOpenMultiExpirationsModal,
  } = useDisclosure({ defaultIsOpen: openExpiresInfo });

  const { labels, multiExpirationsModal } = useGiftcardsPagesLayoutContent();

  const getExpiration = () => {
    const { hasMultiExpirations, formatted } = giftcard;

    if (!hasMultiExpirations) return formatted.expiresAt;

    return (
      <Button
        size="sm"
        variant="link"
        color="blue.800"
        fontWeight="400"
        fontSize="1.25rem"
        onClick={onOpenMultiExpirationsModal}
      >
        {labels.expirationDatesButton}
      </Button>
    );
  };

  return (
    <>
      <MultiExpirationModal
        isOpen={isMultiExpirationsModalOpen}
        onClose={onCloseMultiExpirationsModal}
        depreciations={giftcard.depreciations}
        content={multiExpirationsModal}
      />

      <SplashScreen isOpen={isOpen} onClose={onClose} />

      <AppLayout.Main data-testid="secret-giftcard-page">
        <AppLayout.InfoRow
          rows={[
            {
              key: 'contextName',
              label: labels.context,
              value: giftcard.contextName,
            },
          ]}
        />

        {giftcard.isActivated && giftcard.isExpired && (
          <AppLayout.InfoRow
            rows={[
              {
                key: 'boughtAt',
                label: labels.boughtAt,
                value: giftcard.formatted.boughtAt,
              },
              {
                key: 'expiredAt',
                label: labels.expiredAt,
                value: giftcard.formatted.expiredAt,
              },
            ]}
          />
        )}

        {giftcard.isActivated && !giftcard.isExpired && (
          <AppLayout.InfoRow
            rows={[
              {
                key: 'balance',
                label: labels.balance,
                value: giftcard.formatted.balance,
              },
              {
                key: 'expiresAt',
                label: labels.expiresAt,
                value: getExpiration(),
              },
            ]}
          />
        )}

        <AppLayout.InfoRow
          rows={[
            {
              key: 'shortId',
              label: labels.shortId,
              value: <Text fontFamily="code">{giftcard.shortId}</Text>,
            },
            {
              key: 'giftcardId',
              label: labels.giftcardId,
              value: <Text fontFamily="code">{giftcard.id}</Text>,
            },
          ]}
          hasDivider
          dividerProps={{ borderStyle: 'dashed' }}
        />

        <Hide above="md" ssr>
          <GiftcardActionButtons />

          {isDepotCard && <Divider borderStyle="dashed" />}
        </Hide>

        <Show above="md" ssr>
          <VStack gap="0.5rem">
            <AppLayout.Sponsors />

            <AppLayout.GiftcardDescription giftcard={giftcard} />
          </VStack>
        </Show>
      </AppLayout.Main>

      <Hide above="md" ssr>
        <VStack
          my="1.875rem"
          gap="0.5rem"
          px="1.5rem"
          maxW="max-giftcards-pages-main-sm"
        >
          <AppLayout.Sponsors />

          <AppLayout.GiftcardDescription giftcard={giftcard} />
        </VStack>
      </Hide>
    </>
  );
}
